import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AccessRights, getInitialUserAccessRights } from './access-rights';

export type UserConfigSliceState = {
    /** Listing of applications that user has access to. */
    accessRights: AccessRights,

    /** Latest access Rights error, if any */
    accessRightsError: string | null,

    /** True if the model filters select dropdown is open, false otherwise. */
    isModelFiltersSelectOpen: boolean,
};

export const initialState: UserConfigSliceState = {
    accessRights: getInitialUserAccessRights(),
    accessRightsError: null,
    isModelFiltersSelectOpen: false,
};

/** User-related redux store slice.  */
const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        /**
         * Sets user access rights to given access rights object.
         * @param action payload.accessRights: AccessRights object to use.
         * @param action payload.errorMessage: optional error message.
         */
        accessRightsSet(state, action: PayloadAction<{ accessRights: AccessRights | null, errorMessage?: string }>) {
            const { accessRights, errorMessage } = action.payload;

            if (accessRights !== null) {
                state.accessRights = accessRights;
            }

            state.accessRightsError = errorMessage !== undefined ? errorMessage : null;
        },

        /**
         * Open the model filters select dropdown.
         */
        modelFiltersSelectOpened(state) {
            state.isModelFiltersSelectOpen = true;
        },

        /**
         * Close the model filters select dropdown.
         */
        modelFiltersSelectClosed(state) {
            state.isModelFiltersSelectOpen = false;
        },

        /**
         * Toggle the model filters select dropdown.
         */
        modelFiltersSelectToggled(state) {
            state.isModelFiltersSelectOpen = !state.isModelFiltersSelectOpen;
        },
    },
    selectors: {
        selectAccessRights: (state) => state.accessRights,
        selectAccessRightsError: (state) => state.accessRightsError,
        selectIsModelFiltersSelectOpen: (state) => state.isModelFiltersSelectOpen,
    }

});

export const {
    accessRightsSet,
    modelFiltersSelectOpened,
    modelFiltersSelectClosed,
    modelFiltersSelectToggled,
} = userSlice.actions;

export const { getInitialState, selectors: userSelectors } = userSlice;

export default userSlice.reducer;
